import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ParticipantSummary } from '@proaxiv/server-api'
import type { AppEvent } from '../store/eventsSlice'
import { formatLocalDateTime } from '../utils/datetime'
import { formatFullName, hasGrantedMediaConsent } from '../utils/participantMappers'
import { Button } from './Button'
import { ParticipantTicketButton } from './ParticipantTicketButton'
import { DetailsToggleButton } from './DetailsToggleButton'
import { TablePagination } from './TablePagination'

// ── Icons ─────────────────────────────────────────────────────────────────────

const ClockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

// ── Expanded detail row (desktop) ───────────────────────────────────────────────
// Mirrors the pending "Registrations" table's Details panel (same fields, same layout) so a
// rejected registration is never a dead end for the organiser either — see #215 review
// feedback: rejected rows should keep the same "Details" (and Ticket) access as pending ones.

function ExpandedRow({ reg, eventId }: { reg: ParticipantSummary; eventId: string }) {
  const { t } = useTranslation()
  return (
    <tr className="bg-pax-light-purple/20">
      <td colSpan={4} className="px-6 py-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_last_name')}
            </span>
            <p className="text-pax-black">{reg.lastName || '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_first_name')}
            </span>
            <p className="text-pax-black">{reg.firstName}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_email')}
            </span>
            <p className="text-pax-black">{reg.email}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_company')}
            </span>
            <p className="text-pax-black">{reg.companyName ?? '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_media')}
            </span>
            <p className="text-pax-black">
              {hasGrantedMediaConsent(reg) ? t('reg_media_yes') : t('reg_media_no')}
            </p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_registered_via')}
            </span>
            <p className="text-pax-black">
              {reg.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
            </p>
          </div>
          {reg.rejectionMessage && (
            <div className="col-span-full">
              <span className="text-pax-gray text-xs font-semibold uppercase">
                {t('reg_field_rejection_msg')}
              </span>
              <p className="text-pax-black">{reg.rejectionMessage}</p>
            </div>
          )}
        </div>

        <div className="mt-4">
          <span className="text-pax-gray text-xs font-semibold uppercase">
            {t('acc_additional_actions')}
          </span>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <ParticipantTicketButton eventId={eventId} participantId={reg.id} />
          </div>
        </div>
      </td>
    </tr>
  )
}

interface Props {
  registrations: ParticipantSummary[]
  event: AppEvent
  onReopen: (ids: string[]) => void
  /** Opens the conversation-history modal for one rejected registration — see #158. */
  onConversation: (row: ParticipantSummary) => void
  /** Pager for this panel's own offset/take — independent from the pending table above it on
   *  the Registrations tab (see `usePagedParticipants` / plan §2). */
  total: number
  offset: number
  take: number
  onOffsetChange: (offset: number) => void
  onTakeChange: (take: number) => void
}

/**
 * Lists previously-rejected event registrations and lets an organiser reopen them, moving them
 * back into the pending "Registrations" queue above. Mirrors the "Rejected" panel + "Re-evaluate"
 * action used for organizations on {@link OrganizationApprovalsPage} — reopening is a silent,
 * notification-free transition, so there is no confirmation modal.
 */
export function RejectedRegistrationsPanel({
  registrations,
  event,
  onReopen,
  onConversation,
  total,
  offset,
  take,
  onOffsetChange,
  onTakeChange,
}: Props) {
  const { t, i18n } = useTranslation()
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

  const someSelected = selectedKeys.size > 0
  const allSelected = registrations.length > 0 && selectedKeys.size === registrations.length

  function handleSelectRow(key: string, checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function handleSelectAll(checked: boolean) {
    setSelectedKeys(checked ? new Set(registrations.map((r) => r.id!)) : new Set())
  }

  function handleReopen(ids: string[]) {
    onReopen(ids)
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.delete(id))
      return next
    })
  }

  function toggleExpand(id: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="border-pax-purple/30 flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm">
      {/* Panel header */}
      <div className="border-pax-purple/20 flex items-center justify-between border-b px-5 py-3.5">
        <h2 className="text-pax-black text-sm font-semibold">{t('panel_rejected')}</h2>

        {someSelected && (
          <div className="flex items-center gap-3">
            <span className="text-pax-black text-sm font-medium">
              {t('n_selected', { count: selectedKeys.size })}
            </span>
            <Button
              variant="success"
              size="sm"
              onClick={() => handleReopen(Array.from(selectedKeys))}
            >
              {t('btn_reopen')}
            </Button>
          </div>
        )}
      </div>

      <TablePagination
        total={total}
        offset={offset}
        take={take}
        onOffsetChange={onOffsetChange}
        onTakeChange={onTakeChange}
        toolbarTestId="rejected-toolbar"
      >
        {/* Mobile card list */}
        <div data-testid="mobile-view" className="flex flex-col gap-3 p-3 md:hidden">
          {registrations.length === 0 ? (
            <p className="text-pax-gray py-6 text-center text-sm">{t('reg_rejected_empty')}</p>
          ) : (
            registrations.map((reg) => {
              const isSelected = selectedKeys.has(reg.id!)
              const isExpanded = expandedKeys.has(reg.id!)
              return (
                <div
                  key={reg.id}
                  className={[
                    'border-pax-purple/20 rounded-2xl border p-4',
                    isSelected ? 'bg-pax-light-blue/40' : '',
                  ].join(' ')}
                >
                  <div className="mb-3 flex items-start gap-3">
                    <input
                      type="checkbox"
                      aria-label={t('table_select_row', { name: reg.email })}
                      checked={isSelected}
                      onChange={(e) => handleSelectRow(reg.id!, e.target.checked)}
                      className="accent-pax-blue mt-0.5 h-4 w-4 cursor-pointer rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-pax-black font-medium">
                        {formatFullName(reg.firstName, reg.lastName)}
                      </p>
                      <p className="text-pax-gray text-xs">{reg.email}</p>
                      <p className="text-pax-gray text-xs tabular-nums">
                        {reg.rejectedAt ? formatLocalDateTime(reg.rejectedAt, i18n.language) : '—'}
                      </p>
                      {reg.rejectionMessage && (
                        <p className="text-pax-gray mt-1 text-xs italic">{reg.rejectionMessage}</p>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-pax-purple/20 mb-3 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-xl border bg-slate-50 p-3 text-xs">
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_last_name')}
                        </span>
                        <p className="text-pax-black">{reg.lastName || '—'}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_first_name')}
                        </span>
                        <p className="text-pax-black">{reg.firstName}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_email')}
                        </span>
                        <p className="text-pax-black break-all">{reg.email}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_company')}
                        </span>
                        <p className="text-pax-black">{reg.companyName ?? '—'}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_media')}
                        </span>
                        <p className="text-pax-black">
                          {hasGrantedMediaConsent(reg) ? t('reg_media_yes') : t('reg_media_no')}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_registered_via')}
                        </span>
                        <p className="text-pax-black">
                          {reg.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <DetailsToggleButton
                      variant="block"
                      expanded={isExpanded}
                      onClick={() => toggleExpand(reg.id!)}
                      ariaLabel={`Expand ${reg.email}`}
                    />
                    {isExpanded && (
                      <ParticipantTicketButton
                        eventId={event.id}
                        participantId={reg.id}
                        fullWidth
                      />
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      fullWidth
                      leftIcon={<ClockIcon />}
                      onClick={() => onConversation(reg)}
                    >
                      {t('conversation_action')}
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      fullWidth
                      onClick={() => handleReopen([reg.id!])}
                    >
                      {t('btn_reopen')}
                    </Button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Desktop table */}
        <div data-testid="desktop-view" className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-pax-purple/20 bg-pax-light-purple/60 border-b">
                  <th className="w-10 px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      aria-label={t('select_all')}
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                    />
                  </th>
                  <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                    {t('reg_col_name')}
                  </th>
                  <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                    {t('reg_field_rejection_msg')}
                  </th>
                  {/* Header left visually blank (#274); aria-label keeps a real accessible name. */}
                  <th
                    aria-label={t('reg_col_actions')}
                    className="text-pax-gray px-4 py-3 text-right text-xs font-semibold tracking-wider"
                  ></th>
                </tr>
              </thead>
              <tbody>
                {registrations.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-pax-gray px-4 py-10 text-center text-sm">
                      {t('reg_rejected_empty')}
                    </td>
                  </tr>
                ) : (
                  registrations.map((reg) => {
                    const isSelected = selectedKeys.has(reg.id!)
                    const isExpanded = expandedKeys.has(reg.id!)
                    return (
                      <React.Fragment key={reg.id}>
                        <tr
                          className={[
                            'border-pax-purple/10 border-b transition-colors last:border-0',
                            isSelected ? 'bg-pax-light-blue/40' : 'hover:bg-pax-light-purple/40',
                          ].join(' ')}
                        >
                          <td className="w-10 px-4 py-3">
                            <input
                              type="checkbox"
                              aria-label={t('table_select_row', { name: reg.email })}
                              checked={isSelected}
                              onChange={(e) => handleSelectRow(reg.id!, e.target.checked)}
                              className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                            />
                          </td>
                          <td className="text-pax-black px-4 py-3 text-sm">
                            <p className="font-medium">
                              {formatFullName(reg.firstName, reg.lastName)}
                            </p>
                            <p className="text-pax-gray text-xs">{reg.email}</p>
                            <p className="text-pax-gray text-xs tabular-nums">
                              {reg.rejectedAt
                                ? formatLocalDateTime(reg.rejectedAt, i18n.language)
                                : '—'}
                            </p>
                          </td>
                          <td className="text-pax-black px-4 py-3 text-sm">
                            {reg.rejectionMessage ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <DetailsToggleButton
                                expanded={isExpanded}
                                onClick={() => toggleExpand(reg.id!)}
                                ariaLabel={`Expand ${reg.email}`}
                              />
                              <Button
                                type="button"
                                variant="unstyled"
                                aria-label={t('conversation_action')}
                                title={t('conversation_action')}
                                onClick={() => onConversation(reg)}
                                className="text-pax-gray hover:bg-pax-purple/15 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150 active:scale-90"
                              >
                                <ClockIcon />
                              </Button>
                              <Button
                                variant="success"
                                size="sm"
                                aria-label={`Re-evaluate ${reg.email}`}
                                onClick={() => handleReopen([reg.id!])}
                              >
                                {t('btn_reopen')}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && <ExpandedRow reg={reg} eventId={event.id} />}
                      </React.Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </TablePagination>
    </div>
  )
}
